/**
 * ServiceGroupEditModal — edit a group's name, type, icon and description.
 */
import React, { useEffect, useState } from 'react';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

export default function ServiceGroupEditModal({ group, companyId, onClose, onSaved }) {
  const [meta, setMeta] = useState({ group_types: [] });
  const [form, setForm] = useState({
    name: group.name || '',
    description: group.description || '',
    group_type: group.group_type || 'other',
    icon: '',   // only set if user overrides
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.getServiceGroupsMeta(companyId).then(m => { if (m.group_types) setMeta(m); }).catch(() => {});
  }, [companyId]);

  const selectedType = meta.group_types.find(t => t.value === form.group_type);
  const displayIcon = form.icon || group.icon || selectedType?.icon || '🔷';

  const handleSave = async () => {
    if (!form.name.trim()) { setError('El nombre es obligatorio'); return; }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        group_type: form.group_type,
      };
      if (form.icon.trim()) payload.icon = form.icon.trim();
      await api.updateServiceGroup(group.id, payload, companyId);
      onSaved?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 3200,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div style={{
          background: 'var(--bg-surface)', border: '1px solid var(--border)',
        borderRadius: 12, width: 520, maxWidth: '95vw',
        boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontSize: '1.3rem' }}>{displayIcon}</span>
          <div style={{ flex: 1, fontWeight: 700, fontSize: '1rem' }}>Editar grupo</div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
            <XMarkIcon style={{ width: 18, height: 18 }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 18 }}>
          {error && <div className="alert alert-danger py-2" style={{ fontSize: '0.82rem', marginBottom: 12 }}>⚠ {error}</div>}

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Tipo de grupo</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
              {meta.group_types.map(t => (
                <button key={t.value} type="button"
                  onClick={() => setForm(f => ({ ...f, group_type: t.value }))}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                    padding: '8px 4px', borderRadius: 7, cursor: 'pointer',
                    border: form.group_type === t.value ? '2px solid var(--accent)' : '1px solid var(--border)',
                    background: form.group_type === t.value ? 'var(--blue-50)' : 'var(--bg-toolbar)',
                    fontSize: '0.72rem', color: form.group_type === t.value ? 'var(--accent)' : 'var(--text-secondary)',
                    fontWeight: form.group_type === t.value ? 700 : 400,
                  }}>
                  <span style={{ fontSize: '1.3rem' }}>{t.icon}</span>
                  {t.label.split(' ')[0]}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
              Nombre del grupo *
            </label>
            <input className="form-control" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              autoFocus style={{ fontSize: '0.9rem' }} />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
              Descripción (opcional)
            </label>
            <input className="form-control" value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Breve descripción del grupo..."
              style={{ fontSize: '0.85rem' }} />
          </div>

          <div>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
              Icono personalizado (emoji, opcional)
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input className="form-control" value={form.icon}
                onChange={e => setForm(f => ({ ...f, icon: e.target.value }))}
                placeholder={group.icon || selectedType?.icon || '🔷'}
                style={{ width: 80, fontSize: '1.2rem', textAlign: 'center' }} />
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                Déjalo vacío para mantener el icono actual
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 18px', borderTop: '1px solid var(--border)' }}>
          <button type="button" onClick={onClose} className="dv-btn dv-btn-secondary" style={{ fontSize: '0.82rem' }}>
            Cancelar
          </button>
          <button type="button" onClick={handleSave} disabled={saving}
            className="dv-btn dv-btn-primary" style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 4 }}>
            {saving ? 'Guardando…' : <><CheckIcon style={{ width: 14, height: 14 }} /> Guardar</>}
          </button>
        </div>
      </div>
    </div>
  );
}
